/** Mapeia tipos do banco (companion/fitness) para o formulário (accompaniment/fit_to_work). */
const DB_TO_UI: Record<string, string> = {
  medical_leave: 'medical_leave',
  attendance: 'attendance',
  companion: 'accompaniment',
  fitness: 'fit_to_work',
  fit_to_work: 'fit_to_work',
  accompaniment: 'accompaniment',
  custom: 'custom',
};

const UI_TO_DB: Record<string, string> = {
  medical_leave: 'medical_leave',
  attendance: 'attendance',
  accompaniment: 'companion',
  fit_to_work: 'fitness',
  custom: 'custom',
};

export function certificateTypeFromDb(type?: string): string {
  return DB_TO_UI[type || ''] || type || 'medical_leave';
}

export function certificateTypeToDb(type?: string): string {
  return UI_TO_DB[type || ''] || type || 'medical_leave';
}

export function getCertificateTemplateLabel(t: any): string {
  return t?.name || t?.title || 'Modelo';
}

export function normalizeCertificateTemplate(t: any): any {
  return {
    ...t,
    certificate_type: certificateTypeFromDb(t.certificate_type),
  };
}

/** Campos de dias/CID do modelo → formulário de atestado ou edição de modelo. */
export function certificateTemplateDefaultsFromRecord(template: any): {
  days_off: string;
  cid_code: string;
  cid_description: string;
} {
  return {
    days_off: template?.leave_days != null && template?.leave_days !== '' ? String(template.leave_days) : '',
    cid_code: template?.diagnosis_code || '',
    cid_description: template?.diagnosis || '',
  };
}

/** Payload DB para salvar modelo com dias e CID opcionais. */
export function certificateTemplateSavePayloadFromForm(templateForm: {
  name: string;
  description?: string;
  certificate_type: string;
  content: string;
  days_off?: string;
  cid_code?: string;
  cid_description?: string;
}) {
  const days = Number(templateForm.days_off);
  const cidCode = String(templateForm.cid_code || '').trim();
  const cidDesc = String(templateForm.cid_description || '').trim();
  return {
    name: templateForm.name.trim(),
    description: templateForm.description || null,
    certificate_type: certificateTypeToDb(templateForm.certificate_type),
    content: templateForm.content,
    leave_days: days > 0 ? days : null,
    diagnosis_code: cidCode || null,
    diagnosis: cidDesc || cidCode || null,
    show_diagnosis: Boolean(cidCode),
  };
}

function addDaysIso(dateStr: string, n: number): string {
  const d = new Date(`${String(dateStr).split('T')[0]}T12:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
}

function fmtDate(d: string | undefined | null): string {
  if (!d) return '';
  try {
    return new Date(String(d).split('T')[0] + 'T12:00:00').toLocaleDateString('pt-BR');
  } catch {
    return String(d);
  }
}

function fmtTime(t: string | undefined | null): string {
  if (!t) return '';
  return String(t).slice(0, 5);
}

const FITNESS_LABELS: Record<string, string> = {
  fit: 'apto(a)',
  unfit: 'inapto(a)',
  restricted: 'apto(a) com restrições',
};

export type CertificateTemplateContext = Record<string, string | number | boolean | undefined>;

/** Normaliza campos do formulário/DB e calcula datas de afastamento quando faltarem. */
export function enrichCertificateForTemplate(cert: any): any {
  if (!cert) return {};
  const c = { ...cert };
  const issueDate = c.issue_date || c.attendance_date;
  const days = Number(c.leave_days ?? c.days_off ?? 0) || 0;

  if (c.days_off != null && c.days_off !== '' && !c.leave_days) c.leave_days = c.days_off;
  if (c.cid_code && !c.diagnosis_code) c.diagnosis_code = c.cid_code;
  if (c.cid_description && !c.diagnosis) c.diagnosis = c.cid_description;
  if (c.start_date && !c.leave_start_date) c.leave_start_date = c.start_date;
  if (c.end_date && !c.leave_end_date) c.leave_end_date = c.end_date;

  if (days > 0 && issueDate) {
    if (!c.leave_start_date) c.leave_start_date = issueDate;
    if (!c.leave_end_date) c.leave_end_date = addDaysIso(c.leave_start_date || issueDate, days - 1);
    if (!c.start_date) c.start_date = c.leave_start_date;
    if (!c.end_date) c.end_date = c.leave_end_date;
  }

  return c;
}

/** Monta o mapa de variáveis a partir do atestado, paciente e profissional. */
export function buildCertificateTemplateContext(
  certIn: any,
  patient?: any,
  professional?: any,
): CertificateTemplateContext {
  const cert = enrichCertificateForTemplate(certIn);
  const p = patient || cert?.patient || cert?.patient_snapshot || {};
  const prof = professional || cert?.professional || cert?.professional_snapshot || {};

  const issueDate = cert?.issue_date || cert?.attendance_date;
  const leaveDays = cert?.leave_days ?? cert?.days_off ?? '';
  const leaveStart = cert?.leave_start_date || cert?.start_date || issueDate;
  const leaveEnd = cert?.leave_end_date || cert?.end_date;
  const cidCode = cert?.diagnosis_code || cert?.cid_code || '';
  const cidDesc = cert?.diagnosis || cert?.cid_description || '';
  const showCid = Boolean(cert?.show_diagnosis || cidCode);
  const cid = cidCode
    ? (cidDesc && cidDesc !== cidCode ? `${cidCode} - ${cidDesc}` : cidCode)
    : '';

  const dataInicio = fmtDate(leaveStart);
  const dataFim = fmtDate(leaveEnd);
  const horaInicio = fmtTime(cert?.attendance_start_time);
  const horaFim = fmtTime(cert?.attendance_end_time);

  const periodoEntreDatas =
    dataInicio && dataFim && dataInicio !== dataFim
      ? `de ${dataInicio} a ${dataFim}`
      : dataInicio
        ? `a partir de ${dataInicio}`
        : '';

  const horarioAtendimento =
    horaInicio && horaFim
      ? `das ${horaInicio} às ${horaFim}`
      : horaInicio
        ? `a partir das ${horaInicio}`
        : horaFim
          ? `até às ${horaFim}`
          : '';

  return {
    nome_paciente: p?.full_name || '',
    cpf: p?.cpf || '',
    data_atendimento: fmtDate(issueDate),
    data: fmtDate(issueDate),
    hora_inicio: horaInicio,
    hora_fim: horaFim,
    horario_atendimento: horarioAtendimento,
    dias_afastamento: leaveDays,
    dias: leaveDays,
    data_inicio: dataInicio,
    data_fim: dataFim,
    periodo_entre_datas: periodoEntreDatas,
    periodo_afastamento: leaveDays ? `${leaveDays} dia(s)` : '',
    mostrar_cid: showCid,
    cid,
    nome_acompanhante: cert?.companion_name || '',
    cpf_acompanhante: cert?.companion_cpf || '',
    resultado_aptidao: FITNESS_LABELS[cert?.fitness_result] || cert?.fitness_result || 'apto(a)',
    atividade: cert?.fitness_activity || '',
    restricoes: cert?.fitness_restrictions || '',
    nome_profissional: prof?.full_name || '',
  };
}

/** Substitui variáveis Mustache-style {{var}} e blocos {{#flag}}...{{/flag}}. */
export function resolveCertificateTemplateContent(
  content: string,
  ctx: CertificateTemplateContext,
): string {
  if (!content || !content.includes('{{')) return content;

  let result = content.replace(
    /\{\{#(\w+)\}\}([\s\S]*?)\{\{\/\1\}\}/g,
    (_, key, block) => {
      const val = ctx[key];
      const truthy = val !== undefined && val !== null && val !== '' && val !== false && val !== 0;
      return truthy ? block : '';
    },
  );

  result = result.replace(/\{\{(\w+)\}\}/g, (_, key) => {
    const val = ctx[key];
    return val !== undefined && val !== null ? String(val) : '';
  });

  return result;
}

/** Resolve o corpo do atestado com os dados atuais do formulário. */
export function resolveCertificateFormContent(
  form: any,
  patient?: any,
  professional?: any,
): string {
  const source = form?.content_template || form?.content || '';
  if (!source.includes('{{')) return form?.content || source;
  const ctx = buildCertificateTemplateContext(form, patient, professional);
  return resolveCertificateTemplateContent(source, ctx);
}

/** Atualiza o formulário mantendo o modelo e re-resolvendo variáveis. */
export function syncCertificateFormContent(
  form: any,
  patient?: any,
  professional?: any,
): any {
  if (!form?.content_template) return form;
  return {
    ...form,
    content: resolveCertificateFormContent(form, patient, professional),
  };
}

/** Aplica modelo ao formulário, incluindo dias/CID salvos no cadastro. */
export function applyCertificateTemplateToForm(
  form: any,
  template: any,
  patient?: any,
  professional?: any,
): any {
  const defaults = certificateTemplateDefaultsFromRecord(template);
  const merged = syncCertificateFormContent(
    {
      ...form,
      certificate_type: certificateTypeFromDb(template.certificate_type) || form.certificate_type,
      days_off: defaults.days_off || form.days_off,
      cid_code: defaults.cid_code || form.cid_code,
      cid_description: defaults.cid_description || form.cid_description,
      content_template: template.content || '',
    },
    patient,
    professional,
  );
  return merged;
}

/** Variáveis disponíveis nos modelos (para ajuda na UI). */
export const CERTIFICATE_TEMPLATE_VARIABLES = [
  '{{nome_paciente}}', '{{cpf}}', '{{data_atendimento}}',
  '{{hora_inicio}}', '{{hora_fim}}', '{{horario_atendimento}}',
  '{{dias_afastamento}}', '{{data_inicio}}', '{{data_fim}}', '{{periodo_entre_datas}}',
  '{{#mostrar_cid}} CID: {{cid}}.{{/mostrar_cid}}',
  '{{nome_acompanhante}}', '{{cpf_acompanhante}}', '{{nome_profissional}}',
] as const;
